import { BIOMES, TERRAIN } from '../config';
import { biomeAt } from '../engine/biome';
import { terrainHeight } from '../engine/terrain';
import { SK } from './skin';

// Full-screen island MAP overlay (toggle on M, hold-to-peek on G). A one-time bake samples the biome + height
// field into a top-down image of the WHOLE island; the view is interactive: middle-drag pans, the wheel zooms
// (cursor-centred), left-click drops a named marker (click a marker to remove it). An evenly divided grid with
// per-cell labels (D1, C12, I8 ...) overlays it, faded. Each frame while open we blit the cached static layer
// (island + grid, re-rendered only on pan/zoom/resize) then draw the live markers: the player (a heading
// triangle in a ring), the latest death (an X), and the user's pins. All DOM/canvas, matching the rest of the UI.

const BAKE_RES = [256, 512, 1024];         // LOD pyramid: 256 loads instantly, sharpens to 512 then 1024 in the background
const R = TERRAIN.islandInner * 1.34;      // world half-extent baked: the full land disc + the coast/beaches
const GRID = 12;                           // grid divisions: columns 1..12 across, rows A..L down
const MIN_ZOOM = 1, MAX_ZOOM = 9;

function rgb(n: number): [number, number, number] { return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }

export interface DeathMark { x: number; z: number; }
interface Marker { x: number; z: number; name: string; }

export class MapOverlay {
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly islands: HTMLCanvasElement[] = []; // LOD pyramid of baked biome images (no grid), drawn transformed
  private readonly statics: HTMLCanvasElement;   // cached island+grid at the current view (re-rendered on change)
  private bakedTo = -1;          // highest LOD level fully baked (-1 = none yet)
  private bakeLevel = 0; private bakeRow = 0; private bakeImg: ImageData | null = null; private baking = false; // progressive bake state
  private dirty = true;                          // static layer needs a re-render (pan/zoom/resize/bake)
  visible = false;

  private S = 800;                               // canvas css size (square)
  private dpr = 1;
  private zoom = 1; private cx = 0; private cz = 0;   // PERSISTENT view (M remembers this pan/zoom)
  peeking = false;                                    // G-peek: a transient view centred on the player, zoomed in
  private peekZoom = 3.4; private playerX = 0; private playerZ = 0;

  onPing: ((x: number, z: number) => void) | null = null; // double-click: main relays a team beacon

  private readonly markers: Marker[] = [];
  private naming: HTMLInputElement | null = null;
  private pinSeq = 0;

  private panning = false; private panSX = 0; private panSY = 0; private panCX = 0; private panCZ = 0;

  constructor() {
    for (let l = 0; l < BAKE_RES.length; l++) { const c = document.createElement('canvas'); c.width = c.height = BAKE_RES[l]; this.islands.push(c); }
    this.statics = document.createElement('canvas');
    this.canvas = document.createElement('canvas');
    // Style guide "fullscreen-scrim": the canvas is the glass panel here (borderSoft edge, lg radius);
    // no backdrop blur -- the world keeps rendering behind and blur over the 3D canvas is not free.
    this.canvas.style.cssText = `border:1px solid ${SK.borderSoft};box-shadow:0 10px 60px rgba(0,0,0,0.7);`
      + `border-radius:${SK.rLg};cursor:crosshair;pointer-events:auto;touch-action:none;`;
    this.ctx = this.canvas.getContext('2d')!;
    this.root = document.createElement('div');
    this.root.style.cssText = 'position:fixed;inset:0;z-index:9000;display:none;align-items:center;'
      + `justify-content:center;background:${SK.scrim};pointer-events:none;`;
    this.root.appendChild(this.canvas);
    document.body.appendChild(this.root);

    this.resize();
    window.addEventListener('resize', () => this.resize());
    this.canvas.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    this.canvas.addEventListener('mousedown', (e) => this.onDown(e));
    // DOUBLE-CLICK = team ping (owner QoL): drop a beacon your whole team sees in-world + on the map.
    this.canvas.addEventListener('dblclick', (e) => {
      const [lx, ly] = this.localXY(e);
      this.onPing?.(this.wx(lx), this.wz(ly));
    });
    window.addEventListener('mousemove', (e) => this.onMove(e));
    window.addEventListener('mouseup', (e) => this.onUp(e));
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    // While open the whole overlay swallows mouse input so clicks/scroll on the dim backdrop never reach the
    // game underneath (you can still WASD-walk -- only the keyboard passes through). Canvas events handle
    // themselves above; this catches the backdrop.
    this.root.addEventListener('mousedown', (e) => { if (e.target === this.root) e.stopPropagation(); });
    this.root.addEventListener('wheel', (e) => { if (e.target === this.root) { e.preventDefault(); e.stopPropagation(); } }, { passive: false });
  }

  // ---- sizing -------------------------------------------------------------
  private resize(): void {
    this.S = Math.round(Math.min(window.innerWidth, window.innerHeight) * 0.92);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const c of [this.canvas, this.statics]) {
      c.width = Math.round(this.S * this.dpr); c.height = Math.round(this.S * this.dpr);
    }
    this.canvas.style.width = this.S + 'px'; this.canvas.style.height = this.S + 'px';
    this.dirty = true;
  }

  // ---- world <-> screen (css px) ------------------------------------------
  // Active view = the persistent (M) pan/zoom, OR a transient player-centred zoomed view while G-peeking.
  private aZoom(): number { return this.peeking ? this.peekZoom : this.zoom; }
  private aCx(): number { return this.peeking ? this.playerX : this.cx; }
  private aCz(): number { return this.peeking ? this.playerZ : this.cz; }
  private ppw(): number { return (this.S / (2 * R)) * this.aZoom(); } // pixels per world unit
  private sx(wx: number): number { return this.S / 2 + (wx - this.aCx()) * this.ppw(); }
  private sy(wz: number): number { return this.S / 2 + (wz - this.aCz()) * this.ppw(); }
  private wx(px: number): number { return this.aCx() + (px - this.S / 2) / this.ppw(); }
  private wz(py: number): number { return this.aCz() + (py - this.S / 2) / this.ppw(); }

  // G-peek on/off: a player-centred zoomed view that does NOT touch the persistent M view.
  setPeek(on: boolean): void { if (on !== this.peeking) { this.peeking = on; this.dirty = true; } }

  // ---- island bake: a LOD pyramid (load the low level instantly, sharpen progressively in the background) ----
  // One pixel of the top-down island: deep-blue water by depth, else the biome grass colour with crude relief.
  private pixelColor(wx: number, wz: number, d: Uint8ClampedArray, i: number): void {
    const h = terrainHeight(wx, wz);
    let r: number, g: number, b: number;
    if (h < TERRAIN.seaLevel) {
      const depth = Math.min(1, (TERRAIN.seaLevel - h) / 90);
      r = 60 - depth * 34; g = 116 - depth * 50; b = 168 - depth * 40;
    } else {
      const [cr, cg, cb] = rgb(BIOMES[biomeAt(wx, wz, h)].grass);
      const shade = 0.66 + 0.5 * Math.min(1, (h - TERRAIN.seaLevel) / 320);
      r = Math.min(255, cr * shade); g = Math.min(255, cg * shade); b = Math.min(255, cb * shade);
    }
    d[i] = r; d[i + 1] = g; d[i + 2] = b; d[i + 3] = 255;
  }

  // Full synchronous bake of one LOD level (used for the instant low level).
  private bakeLevelSync(level: number): void {
    const res = BAKE_RES[level], sc = this.islands[level].getContext('2d')!, img = sc.createImageData(res, res), d = img.data;
    for (let py = 0; py < res; py++) { const wz = (py / (res - 1)) * 2 * R - R; for (let px = 0; px < res; px++) this.pixelColor((px / (res - 1)) * 2 * R - R, wz, d, (py * res + px) * 4); }
    sc.putImageData(img, 0, 0);
  }

  // Kick the LOD bake: low level NOW (instant), then 512 + 1024 chunked across frames so the map never hitches.
  private startBake(): void {
    this.bakeLevelSync(0); this.bakedTo = 0; this.dirty = true;
    this.bakeLevel = 1; this.bakeRow = 0; this.bakeImg = null;
    if (!this.baking) { this.baking = true; requestAnimationFrame(this.pumpBake); }
  }

  // Bake a slice of the current LOD level per frame; advance through 512 then 1024, upgrading the display as each
  // finishes. Keeps going even if the map is closed, so reopening is instant + sharp.
  private readonly pumpBake = (): void => {
    if (this.bakeLevel >= BAKE_RES.length) { this.baking = false; return; }
    const level = this.bakeLevel, res = BAKE_RES[level];
    if (!this.bakeImg) this.bakeImg = this.islands[level].getContext('2d')!.createImageData(res, res);
    const d = this.bakeImg.data, chunk = Math.max(2, Math.floor(3072 / res)), end = Math.min(res, this.bakeRow + chunk);
    for (let py = this.bakeRow; py < end; py++) { const wz = (py / (res - 1)) * 2 * R - R; for (let px = 0; px < res; px++) this.pixelColor((px / (res - 1)) * 2 * R - R, wz, d, (py * res + px) * 4); }
    this.bakeRow = end;
    if (this.bakeRow >= res) { this.islands[level].getContext('2d')!.putImageData(this.bakeImg, 0, 0); this.bakedTo = level; this.dirty = true; this.bakeLevel++; this.bakeRow = 0; this.bakeImg = null; }
    if (this.bakeLevel < BAKE_RES.length) requestAnimationFrame(this.pumpBake); else this.baking = false;
  };

  // Re-bake from scratch (the world was re-seeded): drop the pyramid and rebuild from the low level.
  regenerate(): void {
    this.bakedTo = -1; this.bakeLevel = 0; this.bakeRow = 0; this.bakeImg = null; this.baking = false; this.dirty = true;
    if (this.visible) this.startBake();
  }

  // ---- cached static layer: island image (transformed) + grid + cell labels
  private renderStatic(): void {
    const c = this.statics.getContext('2d')!;
    c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    c.clearRect(0, 0, this.S, this.S);
    c.fillStyle = SK.glass; c.fillRect(0, 0, this.S, this.S); // panel face; the island image draws over it

    c.save();
    c.beginPath(); c.rect(0, 0, this.S, this.S); c.clip();

    // island image: spans world [-R, R]; place it under the current pan/zoom.
    const x0 = this.sx(-R), y0 = this.sy(-R), w = 2 * R * this.ppw();
    c.imageSmoothingEnabled = true;
    c.drawImage(this.islands[Math.max(0, this.bakedTo)], x0, y0, w, w); // highest baked LOD (sharpens as it loads)

    // grid in world space (lines move with the map). Columns 1..GRID across, rows A.. down.
    const cell = (2 * R) / GRID;
    c.strokeStyle = 'rgba(255,255,255,0.16)'; c.lineWidth = 1;
    for (let i = 0; i <= GRID; i++) {
      const wv = -R + i * cell;
      const px = this.sx(wv), py = this.sy(wv);
      c.beginPath(); c.moveTo(px, this.sy(-R)); c.lineTo(px, this.sy(R)); c.stroke();
      c.beginPath(); c.moveTo(this.sx(-R), py); c.lineTo(this.sx(R), py); c.stroke();
    }
    // per-cell labels, faded, in each cell's top-left (only cells overlapping the view).
    c.fillStyle = 'rgba(207,194,171,0.38)'; // SK.text faded (canvas wants an rgba literal)
    c.font = `600 ${Math.max(9, Math.min(15, 11 * Math.sqrt(this.zoom)))}px ${SK.font}`;
    c.textAlign = 'left'; c.textBaseline = 'top';
    for (let row = 0; row < GRID; row++) {
      for (let col = 0; col < GRID; col++) {
        const lx = this.sx(-R + col * cell) + 3, ly = this.sy(-R + row * cell) + 2;
        if (lx < -20 || lx > this.S + 20 || ly < -20 || ly > this.S + 20) continue;
        c.fillText(String.fromCharCode(65 + row) + (col + 1), lx, ly);
      }
    }
    c.restore();
    this.dirty = false;
  }

  setVisible(v: boolean): void {
    if (v && this.bakedTo < 0) this.startBake();
    if (v) this.dirty = true;
    this.visible = v;
    this.root.style.display = v ? 'flex' : 'none';
    this.root.style.pointerEvents = v ? 'auto' : 'none'; // capture the mouse while open (keyboard still passes)
    if (!v) this.cancelNaming();
  }

  // Redraw: blit the cached static layer, then the live markers. fwd is the player's world-forward (heading) XZ.
  // `team` = live teammates (yellow dot + name); `pings` = active team beacons (coloured diamonds).
  update(px: number, pz: number, fwdX: number, fwdZ: number, death: DeathMark | null,
    team?: ReadonlyArray<{ x: number; z: number; name: string }>,
    pings?: ReadonlyArray<{ x: number; z: number; color: string; label: string }>): void {
    if (!this.visible) return;
    this.playerX = px; this.playerZ = pz;
    if (this.peeking) this.dirty = true; // peek view follows the player, so re-render the static layer each frame
    if (this.dirty) this.renderStatic();
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.S, this.S);
    ctx.drawImage(this.statics, 0, 0, this.S, this.S);

    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, this.S, this.S); ctx.clip();

    // user pins
    for (const m of this.markers) this.drawPin(ctx, this.sx(m.x), this.sy(m.z), m.name);

    // TEAMMATES (owner ask): yellow dot + name above, for everyone on your base claim.
    if (team) {
      for (const t of team) {
        const tx = this.sx(t.x), ty = this.sy(t.z);
        ctx.beginPath(); ctx.arc(tx, ty, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffd24a'; ctx.strokeStyle = 'rgba(0,0,0,0.75)'; ctx.lineWidth = 1.5; ctx.fill(); ctx.stroke();
        if (t.name) {
          ctx.font = `700 11px ${SK.font}`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
          const w = ctx.measureText(t.name).width;
          ctx.fillStyle = 'rgba(15,12,9,0.75)'; ctx.fillRect(tx - w / 2 - 4, ty - 23, w + 8, 15);
          ctx.fillStyle = '#ffd24a'; ctx.fillText(t.name, tx, ty - 9);
        }
      }
    }

    // TEAM PINGS: a pulsing diamond + label at each active beacon.
    if (pings) {
      const pulse = 1 + 0.25 * Math.sin(performance.now() / 180);
      for (const p of pings) {
        const bx = this.sx(p.x), by = this.sy(p.z), r = 8 * pulse;
        ctx.save(); ctx.translate(bx, by); ctx.rotate(Math.PI / 4);
        ctx.fillStyle = p.color; ctx.strokeStyle = 'rgba(0,0,0,0.75)'; ctx.lineWidth = 1.5;
        ctx.fillRect(-r / 2, -r / 2, r, r); ctx.strokeRect(-r / 2, -r / 2, r, r);
        ctx.restore();
        ctx.font = `700 10px ${SK.font}`; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
        ctx.fillStyle = p.color; ctx.fillText(p.label, bx, by + 8);
      }
    }

    // latest death: a red X with a soft halo + label
    if (death) {
      const dx = this.sx(death.x), dy = this.sy(death.z);
      ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.lineWidth = 5; this.cross(ctx, dx, dy, 9);
      ctx.strokeStyle = '#ff4d4d'; ctx.lineWidth = 3; this.cross(ctx, dx, dy, 9);
      ctx.fillStyle = '#ff4d4d'; ctx.font = `700 11px ${SK.font}`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillText('DEATH', dx, dy + 11);
    }

    // player: ring + heading triangle
    const x = this.sx(px), y = this.sy(pz);
    const ang = Math.atan2(fwdZ, fwdX);
    ctx.save(); ctx.translate(x, y); ctx.rotate(ang);
    ctx.beginPath(); ctx.moveTo(13, 0); ctx.lineTo(-6, -7); ctx.lineTo(-6, 7); ctx.closePath();
    ctx.fillStyle = '#ffd24a'; ctx.strokeStyle = 'rgba(0,0,0,0.7)'; ctx.lineWidth = 1.5; ctx.fill(); ctx.stroke();
    ctx.restore();
    ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff'; ctx.strokeStyle = 'rgba(0,0,0,0.7)'; ctx.lineWidth = 1.5; ctx.fill(); ctx.stroke();
    ctx.restore();

    // controls hint
    ctx.fillStyle = 'rgba(207,194,171,0.55)'; ctx.font = `600 12px ${SK.font}`;
    ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
    ctx.fillText('scroll zoom · middle-drag pan · click to pin/remove · double-click to team-ping', 8, this.S - 7);
  }

  private drawPin(ctx: CanvasRenderingContext2D, x: number, y: number, name: string): void {
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x - 6, y - 13); ctx.lineTo(x + 6, y - 13); ctx.closePath();
    ctx.fillStyle = '#4ec3ff'; ctx.strokeStyle = 'rgba(0,0,0,0.7)'; ctx.lineWidth = 1.5; ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.arc(x, y - 13, 4.5, 0, Math.PI * 2); ctx.fillStyle = '#dff3ff'; ctx.fill(); ctx.stroke();
    if (name) {
      ctx.font = `700 12px ${SK.font}`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
      const w = ctx.measureText(name).width;
      ctx.fillStyle = 'rgba(15,12,9,0.75)'; ctx.fillRect(x - w / 2 - 4, y - 36, w + 8, 16);
      ctx.fillStyle = '#dff3ff'; ctx.fillText(name, x, y - 21);
    }
  }

  private cross(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x - r, y - r); ctx.lineTo(x + r, y + r);
    ctx.moveTo(x + r, y - r); ctx.lineTo(x - r, y + r);
    ctx.stroke();
  }

  // ---- interaction --------------------------------------------------------
  private localXY(e: MouseEvent): [number, number] {
    const b = this.canvas.getBoundingClientRect();
    return [e.clientX - b.left, e.clientY - b.top];
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault(); e.stopPropagation();
    if (this.peeking) return; // peek is a fixed player-centred view; don't let the wheel touch the M view
    const [mx, my] = this.localXY(e);
    const wmx = this.wx(mx), wmz = this.wz(my);
    const f = Math.exp(-e.deltaY * 0.0012);
    this.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.zoom * f));
    // keep the world point under the cursor fixed
    this.cx = wmx - (mx - this.S / 2) / this.ppw();
    this.cz = wmz - (my - this.S / 2) / this.ppw();
    this.clampPan(); this.dirty = true;
  }

  private onDown(e: MouseEvent): void {
    e.stopPropagation();
    if (this.peeking) return; // no pinning/panning while G-peeking (it's a transient look)
    if (e.button === 1) { // middle: pan
      e.preventDefault();
      this.panning = true; const [mx, my] = this.localXY(e);
      this.panSX = mx; this.panSY = my; this.panCX = this.cx; this.panCZ = this.cz;
    } else if (e.button === 0) { // left: place a pin (or click an existing pin to remove it)
      e.preventDefault();
      const [mx, my] = this.localXY(e);
      const hit = this.markers.findIndex((m) => Math.hypot(this.sx(m.x) - mx, this.sy(m.z) - my) < 12
        || Math.hypot(this.sx(m.x) - mx, this.sy(m.z) - 14 - my) < 10);
      if (hit >= 0) { this.markers.splice(hit, 1); return; } // clicked a pin -> remove it
      // Place the pin IMMEDIATELY (so a click always drops a visible pin), then offer an inline rename. Even if
      // the rename box loses focus instantly, the pin stays with its auto-name.
      const m: Marker = { x: this.wx(mx), z: this.wz(my), name: 'P' + (++this.pinSeq) };
      this.markers.push(m);
      this.startRename(m, e.clientX, e.clientY);
    }
  }

  private onMove(e: MouseEvent): void {
    if (!this.panning) return;
    if (!(e.buttons & 4)) { this.panning = false; return; } // middle button released off-window: stop the pan
    const [mx, my] = this.localXY(e);
    const p = this.ppw();
    this.cx = this.panCX - (mx - this.panSX) / p;
    this.cz = this.panCZ - (my - this.panSY) / p;
    this.clampPan(); this.dirty = true;
  }

  private onUp(e: MouseEvent): void { if (e.button === 1) this.panning = false; }

  private clampPan(): void {
    this.cx = Math.max(-R, Math.min(R, this.cx));
    this.cz = Math.max(-R, Math.min(R, this.cz));
  }

  // Inline rename for a just-placed pin (pre-filled + selected). Enter/Esc/blur all commit whatever's typed; the
  // pin already exists, so this only edits its name -- it can never "fail to place".
  private startRename(m: Marker, clientX: number, clientY: number): void {
    this.cancelNaming();
    const input = document.createElement('input');
    input.value = m.name;
    input.style.cssText = 'position:fixed;left:' + clientX + 'px;top:' + (clientY - 38) + 'px;z-index:9100;'
      + `pointer-events:auto;width:150px;padding:4px 8px;font:600 ${SK.fsBody} ${SK.font};color:${SK.textHi};`
      + `background:${SK.surface2};border:1px solid ${SK.accent};border-radius:${SK.rSm};outline:none;`;
    const commit = (): void => {
      if (this.naming !== input) return;
      const v = input.value.trim(); if (v) m.name = v;
      this.naming = null; input.remove();
    };
    input.addEventListener('keydown', (ev) => { ev.stopPropagation(); if (ev.key === 'Enter' || ev.key === 'Escape') commit(); });
    input.addEventListener('blur', commit);
    this.naming = input;
    this.root.appendChild(input); input.focus(); input.select();
  }

  private cancelNaming(): void {
    if (this.naming) { this.naming.remove(); this.naming = null; }
  }
}
